import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function SignUp() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const initialRole = (() => {
    const r = params.get("role");
    return ["hermit", "host", "both"].includes(r) ? r : "both";
  })();

  const [form, setForm] = useState({ name: "", email: "", password: "", role: initialRole });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const r = params.get("role");
    if (r && ["hermit", "host", "both"].includes(r)) {
      setForm((f) => ({ ...f, role: r }));
    }
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="max-w-md mx-auto px-6 py-16">
      <p className="label-overline text-center">Take the vow</p>
      <h1 className="font-display text-5xl text-center mt-3 mb-2">Begin</h1>
      <div className="fleuron mb-8">❦</div>

      <form onSubmit={onSubmit} className="parchment-card space-y-5" data-testid="signup-form">
        <div>
          <label className="label-overline block mb-2">Thy name</label>
          <input data-testid="signup-name" className="field" required value={form.name} onChange={set("name")} />
        </div>
        <div>
          <label className="label-overline block mb-2">Email</label>
          <input data-testid="signup-email" className="field" type="email" autoComplete="email" required value={form.email} onChange={set("email")} />
        </div>
        <div>
          <label className="label-overline block mb-2">Password (8+ characters)</label>
          <input data-testid="signup-password" className="field" type="password" autoComplete="new-password" minLength={8} required value={form.password} onChange={set("password")} />
        </div>
        <div>
          <label className="label-overline block mb-2">I am a…</label>
          <div className="grid grid-cols-3 gap-2" data-testid="signup-role-group">
            {[
              { v: "hermit", l: "Hermit" },
              { v: "host", l: "Host" },
              { v: "both", l: "Either" },
            ].map((opt) => (
              <button
                type="button"
                key={opt.v}
                data-testid={`signup-role-${opt.v}`}
                onClick={() => setForm({ ...form, role: opt.v })}
                className={`py-3 border text-sm font-body transition-colors ${
                  form.role === opt.v ? "bg-ink-black text-parchment-faded border-ink-black" : "border-rule hover:bg-parchment-bone"
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {error && <p data-testid="signup-error" className="text-wax-red text-sm italic">{error}</p>}
        <button data-testid="signup-submit" disabled={submitting} className="wax-button w-full">
          {submitting ? "Sealing…" : "Seal the vow"}
        </button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 editorial-rule" />
          <span className="label-overline">or</span>
          <div className="flex-1 editorial-rule" />
        </div>

        <button type="button" onClick={onGoogle} data-testid="signup-google" className="ghost-button w-full">
          Continue with Google
        </button>
      </form>

      <p className="text-center mt-6 font-body text-ink-iron">
        Already amongst us? <Link to="/sign-in" data-testid="signup-to-signin" className="text-wax-red hover:underline italic">Sign in</Link>
      </p>
    </main>
  );
}
